import React, { useState, useCallback, useRef } from 'react';
import imageCompression from 'browser-image-compression';
import { makeReference, useApolloClient, useMutation } from '@apollo/client';
import { useSegment } from '@hooks/useSegment';

import {
  getPrescriptionUploadLink,
  GetPrescriptionUploadLinkResponse,
} from '@queries/upload-prescription/queries';
import { uploadFile } from '@utils/upload';
import {
  createPrescription,
  CreatePrescriptionMutationParams,
  CreatePrescriptionMutationResponse,
} from '@queries/prescription/mutations';
import { Prescription } from '@interfaces';
import { PHONE_NUMBER } from '@config/environment';

const UPLOAD_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const MAX_FILE_SIZE = 1;
export type LoadFileError = 'DOCUMENT_SIZE' | 'UNSUPPORTED_FILE';
export const isLoadFileError = (error: string): error is LoadFileError => {
  if (['DOCUMENT_SIZE', 'UNSUPPORTED_FILE'].includes(error)) return true;
  return false;
};
export function useUpload() {
  const { track } = useSegment();
  const [loadings, setLoadings] = useState({});
  const currentId = useRef(0);
  const client = useApolloClient();
  const [errorMessage, setErrorMessage] = useState<{
    title: string;
    description: string;
  } | null>(null);

  const [createPrescriptionMutation] = useMutation<
    CreatePrescriptionMutationResponse,
    CreatePrescriptionMutationParams
  >(createPrescription('simple'), {
    update(cache, { data }) {
      cache.modify({
        id: cache.identify(makeReference('ROOT_QUERY')),
        fields: {
          pendingPrescriptions(prescriptions: Prescription[]) {
            return [...prescriptions, data.createPrescription];
          },
        },
      });
    },
  });

  //Logic
  const compressImage = useCallback(async (file: File) => {
    const options = {
      maxSizeMB: 1,
      useWebWorker: true,
    };
    try {
      const compressedFile = await imageCompression(file, options);
      return compressedFile;
    } catch (error) {
      return file;
    }
  }, []);

  const onAskForHelp = useCallback(
    (userId: number) => {
      void track('payment error contact', {
        userId,
        error: 'Prescription upload error',
      });
      window.open(
        `https://api.whatsapp.com/send?phone=${
          PHONE_NUMBER.CLEAN
        }&text=${encodeURI(
          `Hola, tengo problemas para subir la foto de mi receta.`,
        )}`,
        '_blank',
      );
    },
    [track],
  );

  const onError = useCallback(
    (code: unknown) => {
      if (isLoadFileError(code as string)) {
        if (code === 'DOCUMENT_SIZE') {
          void track('prescription upload error', {
            error: 'document size',
          });
          setErrorMessage({
            title: 'No se puede subir el documento',
            description: 'Intenta con un documento más liviano.',
          });
        }
        if (code === 'UNSUPPORTED_FILE') {
          void track('prescription upload error', {
            error: 'unsupported file',
          });
          setErrorMessage({
            title: 'Error en formato',
            description:
              'La imagen debe ser en formato jpeg, png o pdf. Intenta nuevamente o contáctanos para pedir ayuda.',
          });
        }
      } else {
        setErrorMessage({
          title: 'Ha ocurrido un error',
          description:
            'Intenta nuevamente, si el problema persiste contáctanos.',
        });
      }
    },
    [track],
  );

  const handleImageLoader = useCallback(
    async (blobFile: Blob) => {
      const getUploadData = async (file: Blob) => {
        const response = await client.query<GetPrescriptionUploadLinkResponse>({
          query: getPrescriptionUploadLink('uploadPrescriptionScreen'),
          fetchPolicy: 'network-only',
          variables: { params: { format: file.type } },
        });
        return response.data.getPrescriptionUploadLink;
      };
      try {
        const uploadData = await getUploadData(blobFile);
        const uploadFileResponse = await uploadFile(blobFile, uploadData);
        await createPrescriptionMutation({
          variables: {
            params: {
              url: uploadFileResponse,
            },
          },
        });
      } catch (error) {
        onError(error);
      }
    },
    [client, createPrescriptionMutation, onError],
  );

  const loadFile = useCallback(
    async (
      event: React.ChangeEvent<HTMLInputElement>,
      setError: (value: boolean) => void,
    ) => {
      const divisor = 1024 * 1024;
      const file = event.target.files[0];
      if (!file) {
        return;
      }
      if (file && UPLOAD_TYPES.includes(file.type)) {
        setLoadings({ ...loadings, [currentId.current]: true });
        const sizeIsPermitted = file.size / divisor <= MAX_FILE_SIZE;
        const isImage = file.type === 'image/jpeg' || file.type === 'image/png';
        if (!sizeIsPermitted && !isImage) {
          onError('DOCUMENT_SIZE');
          setLoadings((prevState) => ({
            ...prevState,
            [currentId.current]: false,
          }));
          return;
        }
        let finalFile = file;
        if (isImage) {
          finalFile = await compressImage(file);
        }
        await handleImageLoader(finalFile);
      } else {
        onError('UNSUPPORTED_FILE');
      }
      setLoadings((prevState) => ({
        ...prevState,
        [currentId.current]: false,
      }));
      currentId.current = currentId.current + 1;
      setError(false);
    },
    [loadings, handleImageLoader, onError, compressImage],
  );

  return {
    loadings,
    loadFile,
    onAskForHelp,
    onError,
    errorMessage,
    setErrorMessage,
  };
}
